const RELAY_FETCH_ERROR_CONTEXT_KEY = "__whooshRelayFetchErrorContext";
const RELAY_FETCH_ERROR_CAPTURE_DELAY_MS = 1000;

export type RelayFetchErrorContext = {
  url: string;
  method: string;
  operationName: string | null;
  clubSlug: string | null;
  httpStatus: number | null;
  responseContentType: string | null;
  requestId: string | null;
  responseBodyType: string | null;
};

type RelayFetchErrorMetadata = {
  context: RelayFetchErrorContext;
  hasBeenCaptured: boolean;
  fallbackCaptureTimeout?: ReturnType<typeof setTimeout>;
};

type RelayFetchErrorMetadataCarrier = {
  [RELAY_FETCH_ERROR_CONTEXT_KEY]?: RelayFetchErrorMetadata;
};

type SentryScopeWithContext = {
  setContext: (name: string, context: Record<string, unknown>) => void;
};

type SentryEventWithContexts = {
  contexts?: Record<string, unknown>;
};

type SentryEventHint = {
  originalException?: unknown;
  syntheticException?: unknown;
};

type CaptureException = (error: unknown) => unknown;

export const attachRelayFetchContextToError = (
  error: unknown,
  context: RelayFetchErrorContext
) => {
  if (!canCarryRelayFetchErrorMetadata(error)) {
    return;
  }

  Object.defineProperty(error, RELAY_FETCH_ERROR_CONTEXT_KEY, {
    configurable: true,
    enumerable: false,
    value: {
      context,
      hasBeenCaptured: false,
    },
    writable: true,
  });
};

export const captureRelayFetchErrorIfUnhandled = (
  error: unknown,
  captureException: CaptureException
) => {
  const metadata = getRelayFetchErrorMetadata(error);

  if (!metadata || metadata.fallbackCaptureTimeout) {
    return;
  }

  metadata.fallbackCaptureTimeout = setTimeout(() => {
    metadata.fallbackCaptureTimeout = undefined;

    if (!metadata.hasBeenCaptured) {
      captureException(error);
    }
  }, RELAY_FETCH_ERROR_CAPTURE_DELAY_MS);
};

export const applyRelayFetchContextToSentryScope = (
  scope: SentryScopeWithContext,
  error: unknown
) => {
  const metadata = getRelayFetchErrorMetadata(error);

  if (metadata) {
    markRelayFetchErrorCaptured(metadata);
    scope.setContext("relay_fetch", metadata.context);
  }
};

export const applyRelayFetchContextToSentryEvent = <
  Event extends SentryEventWithContexts
>(
  event: Event,
  hint?: SentryEventHint
) => {
  const metadata =
    getRelayFetchErrorMetadata(hint?.originalException) ??
    getRelayFetchErrorMetadata(hint?.syntheticException);

  if (!metadata) {
    return event;
  }

  markRelayFetchErrorCaptured(metadata);

  return {
    ...event,
    contexts: {
      ...event.contexts,
      relay_fetch: metadata.context,
    },
  };
};

const getRelayFetchErrorMetadata = (
  error: unknown
): RelayFetchErrorMetadata | null => {
  if (!canCarryRelayFetchErrorMetadata(error)) {
    return null;
  }

  return (
    (error as RelayFetchErrorMetadataCarrier)[RELAY_FETCH_ERROR_CONTEXT_KEY] ??
    null
  );
};

const markRelayFetchErrorCaptured = (metadata: RelayFetchErrorMetadata) => {
  metadata.hasBeenCaptured = true;

  if (metadata.fallbackCaptureTimeout) {
    clearTimeout(metadata.fallbackCaptureTimeout);
    metadata.fallbackCaptureTimeout = undefined;
  }
};

const canCarryRelayFetchErrorMetadata = (
  value: unknown
): value is RelayFetchErrorMetadataCarrier => {
  return (
    value !== null && (typeof value === "object" || typeof value === "function")
  );
};
